"use client";
import { colorForNotesIdentity, type NotesPeerPresence } from "@client/utils/notesPresence";
import { Room } from "livekit-client";
import { useState, useCallback, useRef, useMemo, useEffect } from "react";

interface UseNotesOptions {
  room: Room;
  showNotes: boolean;
  liveCursorsDisabled?: boolean;
}

export type NotesPresencePublish = {
  selectionStart: number;
  selectionEnd: number;
  isTyping: boolean;
  isViewingNotes: boolean;
  displayName?: string;
};

interface UseNotesReturn {
  sharedNotes: string;
  unreadNotes: boolean;
  remoteNotesPresence: NotesPeerPresence[];
  handleUpdateNotes: (notes: string) => void;
  publishNotesPresence: (payload: NotesPresencePublish) => void;
  handleNotesDataReceived: (message: Record<string, unknown>) => void;
}

const PUBLISH_OPTIONS = { reliable: true } as const;
const textEncoder = new TextEncoder();

/**
 * Manages shared notes state and updates via data channel.
 */
export const useNotes = ({ room, showNotes, liveCursorsDisabled = false }: UseNotesOptions): UseNotesReturn => {
  const [sharedNotes, setSharedNotes] = useState("");
  const [unreadNotes, setUnreadNotes] = useState(false);
  const [remotePresence, setRemotePresence] = useState<Map<string, NotesPeerPresence>>(new Map());

  const roomRef = useRef(room);
  useEffect(() => {
    roomRef.current = room;
  }, [room]);

  const showNotesRef = useRef(showNotes);
  useEffect(() => {
    showNotesRef.current = showNotes;
  }, [showNotes]);

  const liveCursorsDisabledRef = useRef(liveCursorsDisabled);
  useEffect(() => {
    liveCursorsDisabledRef.current = liveCursorsDisabled;
    if (liveCursorsDisabled) {
      setRemotePresence(new Map());
    }
  }, [liveCursorsDisabled]);

  useEffect(() => {
    if (showNotes) {
      setUnreadNotes(false);
    }
  }, [showNotes]);

  const handleUpdateNotes = useCallback((notes: string) => {
    setSharedNotes(notes);

    const data = textEncoder.encode(
      JSON.stringify({
        type: "notes-update",
        notes
      })
    );
    roomRef.current.localParticipant.publishData(data, PUBLISH_OPTIONS);
  }, []);

  const publishNotesPresence = useCallback((payload: NotesPresencePublish) => {
    if (liveCursorsDisabledRef.current) return;
    const lp = roomRef.current.localParticipant;
    const data = textEncoder.encode(
      JSON.stringify({
        type: "notes-presence",
        identity: lp.identity,
        name: payload.displayName?.trim() || lp.name || lp.identity,
        selectionStart: payload.selectionStart,
        selectionEnd: payload.selectionEnd,
        isTyping: payload.isTyping,
        isViewingNotes: payload.isViewingNotes,
        updatedAt: Date.now()
      })
    );
    roomRef.current.localParticipant.publishData(data, PUBLISH_OPTIONS);
  }, []);

  const handleNotesDataReceived = useCallback((message: Record<string, unknown>) => {
    if (message.type === "notes-update") {
      setSharedNotes(message.notes as string);
      if (!showNotesRef.current) {
        setUnreadNotes(true);
      }
      return;
    }

    if (message.type === "notes-presence") {
      if (liveCursorsDisabledRef.current) return;
      const identity = typeof message.identity === "string" ? message.identity : "";
      if (!identity || identity === roomRef.current.localParticipant.identity) return;

      const remoteParticipant = roomRef.current.getParticipantByIdentity(identity);
      const messageName = typeof message.name === "string" ? message.name.trim() : "";
      const participantName = remoteParticipant?.name?.trim() ?? "";
      const name = messageName || participantName || identity;
      const selectionStart = Number(message.selectionStart);
      const selectionEnd = Number(message.selectionEnd);
      const updatedAt = Number(message.updatedAt) || Date.now();

      setRemotePresence((prev) => {
        const next = new Map(prev);
        next.set(identity, {
          identity,
          name,
          color: colorForNotesIdentity(identity),
          selectionStart: Number.isFinite(selectionStart) ? selectionStart : 0,
          selectionEnd: Number.isFinite(selectionEnd) ? selectionEnd : 0,
          isTyping: Boolean(message.isTyping),
          isViewingNotes: Boolean(message.isViewingNotes),
          updatedAt
        });
        return next;
      });
    }
  }, []);

  const remoteNotesPresence = useMemo(() => Array.from(remotePresence.values()), [remotePresence]);

  return useMemo(
    () => ({
      sharedNotes,
      unreadNotes,
      remoteNotesPresence,
      handleUpdateNotes,
      publishNotesPresence,
      handleNotesDataReceived
    }),
    [sharedNotes, unreadNotes, remoteNotesPresence, handleUpdateNotes, publishNotesPresence, handleNotesDataReceived]
  );
};
